import React from 'react';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import SharedTimeOffCalendar from '../calendar/SharedTimeOffCalendar';
import DirectorApprovalQueuePanel from './DirectorApprovalQueuePanel';
import DirectorEntryCard from './DirectorEntryCard';
import DirectorEventEntryFields from './DirectorEventEntryFields';
import DirectorRequestFilters from './DirectorRequestFilters';
import DirectorPanelState from './DirectorPanelState';
import DirectorRequestSummaryCards from './DirectorRequestSummaryCards';
import DirectorRequestTable from './DirectorRequestTable';
import DirectorSmsCalloutPanel from './DirectorSmsCalloutPanel';
import RequestQueueTabs from '../requests/RequestQueueTabs';
import ReviewActionBar from '../requests/ReviewActionBar';
import { isAttendanceEvent, isVacationEvent } from '../requests/requestUtils';
import OperationalRequestDetail from '../workspace/OperationalRequestDetail';
import OperationalRequestEntrySection from '../workspace/OperationalRequestEntrySection';

export default function DirectorRequestsSection({
  filteredRequests,
  requestFilter,
  setRequestFilter,
  requestSummary,
  requestSearch,
  setRequestSearch,
  requestTypeFilter,
  setRequestTypeFilter,
  selectedRequestId,
  setSelectedRequestId,
  selectedRequest,
  selectedRequestCanReview,
  requestActionError,
  requestActionLoading,
  pendingAction,
  handleRequestDecision,
  reviewNote,
  setReviewNote,
  requestHistoryVersion,
  ownRequestOutsideScope,
  currentUserManager,
  allLocationsLabel,
  handleDirectorVacationSubmitted,
  eventEntryError,
  eventEntryMessage,
  eventEntryLoading,
  attendanceForm,
  setAttendanceForm,
  manualPtoForm,
  setManualPtoForm,
  handleAttendanceSubmit,
  handleManualPtoSubmit,
  managerEntryOptions,
  selectedLocationCode,
  locationLabel,
  user,
  onInlineDecision,
  onNavigate
}) {
  const updateAttendanceForm = React.useCallback((field, value) => {
    setAttendanceForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'managerId' ? { employeeProfileId: '', subjectLabel: '' } : {}),
      ...(field === 'startDate' && !current.endDate ? { endDate: value } : {})
    }));
  }, [setAttendanceForm]);

  const updateManualPtoForm = React.useCallback((field, value) => {
    setManualPtoForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'managerId' ? { employeeProfileId: '', subjectLabel: '' } : {}),
      ...(field === 'startDate' && !current.endDate ? { endDate: value } : {})
    }));
  }, [setManualPtoForm]);

  const handleOpenPersonWorkspace = React.useCallback((subject) => {
    const targetPath = buildPersonWorkspacePath({
      employeeProfileId: subject?.employeeProfileId,
      managerId: subject?.managerId,
      tab: isAttendanceEvent(subject)
        ? 'attendance'
        : isVacationEvent(subject)
          ? 'time-off'
          : null
    });

    if (targetPath === '/employees') {
      return;
    }

    onNavigate?.(targetPath);
  }, [onNavigate]);

  return (
    <>
      <div className="role-command-grid director-requests-layout">
        <div className="role-command-grid-main role-stack">
          <div className="role-section">
            <div className="role-section-header">
              <h2>Live Request Monitor</h2>
              <div className="role-section-actions">
                <span>{filteredRequests.length} shown</span>
                <a href="#shared-time-off-calendar" className="role-section-jump-link">
                  <i className="fas fa-calendar-alt" aria-hidden="true" /> Calendar
                </a>
              </div>
            </div>
            <p className="role-section-copy">
              Review live requests, approval status, and event history for the current scope.
            </p>
            <p className="director-muted">Use Data Management &amp; Audit for requests CSV export.</p>
            <DirectorRequestSummaryCards summary={requestSummary} />
            <RequestQueueTabs
              includeAll
              selectedQueue={requestFilter}
              summary={requestSummary}
              onSelect={setRequestFilter}
            />
            <DirectorRequestFilters
              requestSearch={requestSearch}
              requestTypeFilter={requestTypeFilter}
              setRequestSearch={setRequestSearch}
              setRequestTypeFilter={setRequestTypeFilter}
            />
            <DirectorRequestTable
              filteredRequests={filteredRequests}
              onOpenPersonWorkspace={handleOpenPersonWorkspace}
              onInlineDecision={onInlineDecision}
              onSelectRequest={setSelectedRequestId}
              requestActionLoading={requestActionLoading}
              selectedRequestId={selectedRequestId}
            />
            <OperationalRequestDetail
              request={selectedRequest}
              requestTitle="Selected Request Detail"
              emptyMessage="Select a request from the live monitor to view its current outcome and event history."
              onOpenPersonWorkspace={handleOpenPersonWorkspace}
              footer={selectedRequest ? (
                selectedRequestCanReview ? (
                  <ReviewActionBar
                    errorMessage={requestActionError}
                    isSubmitting={requestActionLoading}
                    pendingAction={pendingAction}
                    onApprove={() => handleRequestDecision('approve')}
                    onDeny={() => handleRequestDecision('deny')}
                    onReviewNoteChange={setReviewNote}
                    reviewNote={reviewNote}
                  />
                ) : (
                  <p className="role-section-copy role-inline-message">
                    This request already has a final outcome. Review history remains available on the right.
                  </p>
                )
              ) : null}
              historyRefreshKey={requestHistoryVersion}
              historyTitle="Request Decision History"
              requestContext={selectedRequest ? `${selectedRequest.managerName} — ${selectedRequest.startDate} to ${selectedRequest.endDate}` : null}
            />
          </div>
        </div>

        <div className="role-command-grid-side role-stack">
          <div className="role-section">
            <OperationalRequestEntrySection
              canSubmit
              currentManager={currentUserManager}
              helperContent={ownRequestOutsideScope ? (
                <p className="director-muted">
                  Your linked manager is assigned to {currentUserManager?.locationCode}. Switch the location scope to {currentUserManager?.locationCode} or {allLocationsLabel} if you want to see this request in the current view after submission.
                </p>
              ) : null}
              onSubmitted={handleDirectorVacationSubmitted}
              scopeCode={currentUserManager?.roleTitle || 'Current profile'}
              submitCopy="Submit your own PTO request from this workflow."
              title="Request Your Own Time Off"
            />
          </div>

          <div className="role-section director-attendance-entry-section">
            <div className="role-section-header">
              <h2>Attendance & Manual PTO</h2>
              <div className="role-section-actions">
                <span>Your workspace entry</span>
              </div>
            </div>
            <p className="role-section-copy">
              Quick-entry tools for attendance incidents and approved manual PTO in the current scope.
            </p>
            <p className="director-muted">Use Data Management &amp; Audit for attendance CSV export.</p>
            <DirectorPanelState kind="success" message={!eventEntryError ? eventEntryMessage : ''} />
            <DirectorPanelState kind="error" message={eventEntryError} />
            <div className="director-split-grid director-phase-two-grid">
              <div id="log-attendance-entry">
                <DirectorEntryCard title="Log Attendance" statusLabel="Recorded">
                  <form className="director-form-grid" onSubmit={handleAttendanceSubmit}>
                    <DirectorEventEntryFields
                      form={attendanceForm}
                      managerEntryOptions={managerEntryOptions}
                      onChange={updateAttendanceForm}
                      reasonPlaceholder="Describe the attendance incident"
                      submitLabel="Record Attendance"
                      submitting={eventEntryLoading}
                      extraFields={(
                        <label>
                          Event Type
                          <select value={attendanceForm.eventType} onChange={(event) => updateAttendanceForm('eventType', event.target.value)}>
                            <option value="sick_call">Sick Call</option>
                            <option value="other_absence">Other Absence</option>
                            <option value="late_arrival">Late Arrival</option>
                          </select>
                        </label>
                      )}
                    />
                  </form>
                </DirectorEntryCard>
              </div>

              <div id="manual-pto-entry">
                <DirectorEntryCard title="Manual PTO" statusLabel="Approved Entry">
                  <form className="director-form-grid" onSubmit={handleManualPtoSubmit}>
                    <DirectorEventEntryFields
                      form={manualPtoForm}
                      managerEntryOptions={managerEntryOptions}
                      onChange={updateManualPtoForm}
                      reasonPlaceholder="Required audit reason for manual PTO entry"
                      submitLabel="Record Manual PTO"
                      submitting={eventEntryLoading}
                    />
                  </form>
                </DirectorEntryCard>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DirectorSmsCalloutPanel selectedLocationCode={selectedLocationCode} />

      <div className="director-surface-divider">
        <div className="director-surface-divider-line" />
        <div className="director-surface-divider-label">
          <strong>Workflow Approvals</strong>
          <span>Goals, actuals, attendance events, and performance impacts — items from all operational workflows that need a decision before they affect scorecards.</span>
        </div>
        <div className="director-surface-divider-note">
          The Live Request Monitor above handles time-off requests only. This queue handles everything else.
        </div>
      </div>

      <DirectorApprovalQueuePanel
        selectedLocationCode={selectedLocationCode}
        locationLabel={locationLabel}
        user={user}
      />

      <div id="shared-time-off-calendar">
        <SharedTimeOffCalendar
          locationCode={selectedLocationCode}
          locationLabel={locationLabel}
          onOpenPersonWorkspace={handleOpenPersonWorkspace}
          roleLabel="Leadership workspace"
          subtitle="Shared calendar view across approved time off, blackout days, and visible coverage-rule markers."
          title="Shared Time-Off Calendar"
        />
      </div>
    </>
  );
}
